import { useId } from "react"

import {
  Button,
  Input,
  Label,
  RadioGroup,
  RadioGroupItem,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@coval/ui"

import {
  RECURRENCE_TYPE,
  SCHEDULE_AM_PM,
  SCHEDULE_PRESET_OPTIONS,
  SCHEDULE_TYPE,
  TIME_UNIT,
  TIME_UNIT_LABELS,
  type RecurrenceType,
  type ScheduleAmPm,
  type ScheduleType,
  type TimeUnit,
} from "@/lib/schedule"
import { DayPresetSelector, DaySelector } from "@/components/shared/DaySelector"

import type {
  ScheduleFormActions,
  ScheduleFormState,
  ScheduleFormValidation,
} from "../hooks/useScheduleForm"

interface ScheduleFormFieldsProps {
  actions: ScheduleFormActions
  state: ScheduleFormState
  timezoneAbbr: string
  validation: ScheduleFormValidation
}

export function ScheduleFormFields({
  actions,
  state,
  timezoneAbbr,
  validation,
}: ScheduleFormFieldsProps) {
  const customValueId = useId()
  const customUnitId = useId()
  const amPmId = useId()
  const hourId = useId()
  const minuteId = useId()
  const dailyId = useId()
  const weeklyId = useId()

  return (
    <Tabs
      value={state.scheduleType}
      onValueChange={(value) => actions.setScheduleType(value as ScheduleType)}
    >
      <TabsList variant="underline" aria-label="Schedule type">
        <TabsTrigger value={SCHEDULE_TYPE.INTERVAL}>Interval-based</TabsTrigger>
        <TabsTrigger value={SCHEDULE_TYPE.TIME_OF_DAY}>Time of Day</TabsTrigger>
      </TabsList>

      <TabsContent value={SCHEDULE_TYPE.INTERVAL}>
        <div className="space-y-6 pt-4">
          <div className="space-y-3">
            <div>
              <p className="text-sm font-semibold">Interval</p>
              <p className="text-xs text-coval-text-muted">
                Select a common interval or choose Custom
              </p>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {SCHEDULE_PRESET_OPTIONS.map((preset, index) => (
                <Button
                  key={preset.label}
                  variant={
                    state.selectedPreset === index ? "default" : "secondary"
                  }
                  onClick={() => actions.setSelectedPreset(index)}
                  className="h-12"
                  aria-label={`Set interval to ${preset.label}`}
                >
                  {preset.label}
                </Button>
              ))}
              <Button
                variant={
                  state.selectedPreset === null ? "default" : "secondary"
                }
                onClick={() => actions.setSelectedPreset(null)}
                className="h-12"
                aria-label="Set a custom interval"
              >
                Custom
              </Button>
            </div>
          </div>

          {state.selectedPreset === null && (
            <div className="space-y-3">
              <div>
                <p className="text-sm font-semibold">Custom Interval</p>
                <p className="text-xs text-coval-text-muted">
                  Set a specific interval
                </p>
              </div>
              <div className="flex items-start gap-3">
                <div className="flex-1 space-y-2">
                  <Label htmlFor={customValueId}>Run every</Label>
                  <Input
                    id={customValueId}
                    type="text"
                    inputMode="numeric"
                    value={state.customValue}
                    onChange={(event) => {
                      const inputValue = event.target.value
                      if (/^\d*$/.test(inputValue)) {
                        actions.setCustomValue(inputValue)
                      }
                    }}
                    error={!validation.isCustomValid}
                  />
                  {!validation.isCustomValid && (
                    <p className="text-xs text-coval-danger-text">
                      Must be between 15 minutes and 30 days
                    </p>
                  )}
                </div>
                <div className="w-32 space-y-2">
                  <Label htmlFor={customUnitId} id={`${customUnitId}-label`}>
                    Unit
                  </Label>
                  <Select
                    value={state.customUnit}
                    onValueChange={(value) =>
                      actions.setCustomUnit(value as TimeUnit)
                    }
                  >
                    <SelectTrigger
                      id={customUnitId}
                      aria-labelledby={`${customUnitId}-label`}
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={TIME_UNIT.MINUTES}>
                        {TIME_UNIT_LABELS[TIME_UNIT.MINUTES].plural}
                      </SelectItem>
                      <SelectItem value={TIME_UNIT.HOURS}>
                        {TIME_UNIT_LABELS[TIME_UNIT.HOURS].plural}
                      </SelectItem>
                      <SelectItem value={TIME_UNIT.DAYS}>
                        {TIME_UNIT_LABELS[TIME_UNIT.DAYS].plural}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <p className="text-xs text-coval-text-muted">
                Minimum: 15 minutes | Maximum: 30 days
              </p>
            </div>
          )}
        </div>
      </TabsContent>

      <TabsContent value={SCHEDULE_TYPE.TIME_OF_DAY}>
        <div className="space-y-6 pt-4">
          <div className="space-y-3">
            <div>
              <p className="text-sm font-semibold">Schedule Time</p>
              <p className="text-xs text-coval-text-muted">
                Times shown in {timezoneAbbr}
              </p>
            </div>
            <div className="flex items-end gap-2">
              <div className="w-20 space-y-2">
                <Label htmlFor={hourId}>Hour</Label>
                <Input
                  id={hourId}
                  type="text"
                  inputMode="numeric"
                  value={state.scheduleHour}
                  onChange={(event) => {
                    const value = event.target.value
                    if (/^\d*$/.test(value) && value.length <= 2) {
                      actions.setScheduleHour(value)
                    }
                  }}
                  placeholder="9"
                />
              </div>
              <span className="pb-1 text-2xl">:</span>
              <div className="w-20 space-y-2">
                <Label htmlFor={minuteId}>Minute</Label>
                <Input
                  id={minuteId}
                  type="text"
                  inputMode="numeric"
                  value={state.scheduleMinute}
                  onChange={(event) => {
                    const value = event.target.value
                    if (/^\d*$/.test(value) && value.length <= 2) {
                      actions.setScheduleMinute(value)
                    }
                  }}
                  placeholder="00"
                />
              </div>
              <div className="w-24 space-y-2">
                <Label htmlFor={amPmId} id={`${amPmId}-label`}>
                  AM/PM
                </Label>
                <Select
                  value={state.scheduleAmPm}
                  onValueChange={(value) =>
                    actions.setScheduleAmPm(value as ScheduleAmPm)
                  }
                >
                  <SelectTrigger
                    id={amPmId}
                    aria-labelledby={`${amPmId}-label`}
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={SCHEDULE_AM_PM.AM}>AM</SelectItem>
                    <SelectItem value={SCHEDULE_AM_PM.PM}>PM</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <div>
              <p className="text-sm font-semibold">Recurrence</p>
              <p className="text-xs text-coval-text-muted">
                How often should this run?
              </p>
            </div>
            <RadioGroup
              value={state.recurrenceType}
              onValueChange={(value) =>
                actions.setRecurrenceType(value as RecurrenceType)
              }
              className="flex gap-4"
            >
              <div className="flex items-center gap-2">
                <RadioGroupItem
                  value={RECURRENCE_TYPE.DAILY}
                  id={dailyId}
                  aria-label="Daily"
                />
                <label htmlFor={dailyId} className="cursor-pointer text-sm">
                  Daily
                </label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem
                  value={RECURRENCE_TYPE.WEEKLY}
                  id={weeklyId}
                  aria-label="Weekly"
                />
                <label htmlFor={weeklyId} className="cursor-pointer text-sm">
                  Weekly
                </label>
              </div>
            </RadioGroup>
          </div>

          {state.recurrenceType === RECURRENCE_TYPE.WEEKLY && (
            <div className="space-y-3">
              <div>
                <p className="text-sm font-semibold">Days of Week</p>
                <p className="text-xs text-coval-text-muted">
                  Select which days to run
                </p>
              </div>
              <DayPresetSelector
                selectedDays={state.selectedDays}
                onDaysChange={actions.setSelectedDays}
              />
              <DaySelector
                selectedDays={state.selectedDays}
                onDaysChange={actions.setSelectedDays}
              />
              {state.selectedDays.size === 0 && (
                <p className="text-xs text-coval-danger-text">
                  Please select at least one day
                </p>
              )}
            </div>
          )}
        </div>
      </TabsContent>
    </Tabs>
  )
}
